import clsx from 'clsx';
import { Check } from 'lucide-react';
import type { ImgHTMLAttributes } from 'react';
import { BackgroundGradient } from '../BackgroundGradient';
import { SeasonalEffect, type SeasonalEffectType } from '../SeasonalEffect';
import styles from './Hero.module.scss';

interface HeroProps {
  children: React.ReactNode;
  ariaLabel?: string;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  thick?: boolean;
  tightLtMd?: boolean; // Adds equal padding on the block axis in the breakpoint lt-md
  rootGutter?: boolean; // Positions element at the bottom of the Hero with equal padding
  fetchPriority?: ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  highlights?: string[];
  highlightsAriaLabel?: string;
  seasonalEffect?: SeasonalEffectType;
}

export const Hero: React.FC<HeroProps> = ({
  children,
  ariaLabel,
  ariaLabelledby,
  ariaDescribedby,
  thick,
  tightLtMd,
  rootGutter,
  fetchPriority,
  highlights,
  highlightsAriaLabel,
  seasonalEffect = 'none',
}) => {
  const useAnimatedGradient = true;

  return (
    <section
      className={clsx(styles.hero, 'page-hero', {
        [styles.heroTightLtMd]: tightLtMd,
        [styles.heroRootGutter]: rootGutter,
        [styles.heroThick]: thick,
      })}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
    >
      <div className={styles.heroContent}>
        {useAnimatedGradient ? (
          <BackgroundGradient />
        ) : (
          <img
            src="/markets/wayke/gradient/large.jpg"
            alt="Backdround gradient"
            className={styles.gradient}
            aria-hidden="true"
            role="presentation"
            fetchPriority={fetchPriority}
          />
        )}
        <SeasonalEffect effect={seasonalEffect} />
        {children}
      </div>
      {highlights && (
        <footer className={styles.footer}>
          <ul className={styles.highlights} aria-label={highlightsAriaLabel}>
            {highlights.map((highlight, index) => (
              <li key={index} className={styles.highlight}>
                <Check aria-hidden="true" />
                {highlight}
              </li>
            ))}
          </ul>
        </footer>
      )}
    </section>
  );
};
